/**
 * One place that turns a timestamp into words.
 *
 * Every date in the console came out of a bare `toLocaleDateString()` — 22 call
 * sites, no shared helper — which renders in the VIEWER's locale. On a machine
 * set to US English that is M/D/YYYY, so a Dutch client's renewal read
 * "Renews 6/2/2026": 6 February or 2 June, with nothing on screen to say which.
 * Adholics' clients are in NL, BE and NO, where that order is simply wrong.
 *
 * The format is fixed rather than delegated to the viewer's locale, because
 * the ambiguity IS the defect and a locale-sensitive format keeps it for
 * whoever has the wrong locale. `2 Jun 2026` cannot be misread in any of them,
 * and 24-hour time is what all three countries use.
 *
 * `en-GB` here is a formatting recipe, not a claim about language: it is what
 * produces day-month-year with a short month name. The options are explicit so
 * the output does not drift if the environment's default changes.
 */

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Seconds since the epoch, milliseconds, an ISO string, or a Date. */
export type Timestamp = number | string | Date;

function toDate(value: Timestamp): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (!Number.isFinite(value) || value === 0) return null;
  // Unix SECONDS is what this API sends everywhere; anything past the year 3000
  // in seconds is really milliseconds, which a couple of call sites pass.
  const d = new Date(value > 32_503_680_000 ? value : value * 1000);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2 Jun 2026", or an em dash for nothing. */
export function formatDate(value: Timestamp | null | undefined): string {
  const d = value == null ? null : toDate(value);
  return d ? DATE.format(d) : '—';
}

/** "14:05" — 24-hour, for a "fetched at" stamp where the day is obvious. */
export function formatTime(value: Timestamp | null | undefined): string {
  const d = value == null ? null : toDate(value);
  return d ? TIME.format(d) : '—';
}

/** "2 Jun 2026, 14:05". */
export function formatDateTime(value: Timestamp | null | undefined): string {
  const d = value == null ? null : toDate(value);
  return d ? DATE_TIME.format(d) : '—';
}

/**
 * "12m ago", "3d ago", then an absolute date once relative stops being useful.
 * Beyond a week "45d ago" makes a reader do arithmetic to find out when.
 */
export function formatRelative(value: Timestamp | null | undefined): string {
  const d = value == null ? null : toDate(value);
  if (!d) return '—';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 0) return formatDate(d);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return formatDate(d);
}
